import type { FC } from 'react'

import { Bot } from 'lucide-react'
import { pad } from '@/lib/format'
import ButtonLink from '@/components/landing/button-link'
import CodeWindow from '@/components/landing/code-window'
import SectionHeading, { Accent } from '@/components/landing/section-heading'
import {
    EMBED_SNIPPET,
    LINKS,
    ONBOARDING_STEPS,
    SESSION_SNIPPET
} from '@/constants/kinematic'

const SNIPPETS = [
    { id: 'embed', file: 'casino.html', code: EMBED_SNIPPET },
    { id: 'session', file: 'session-token.mjs', code: SESSION_SNIPPET }
]

const IntegrationSection: FC = () => {
    return (
        <section id='integration' aria-labelledby='integration-title' className='relative border-t border-white/[0.06] py-24 md:py-32'>
            <div className='mx-auto max-w-[1320px] px-5 md:px-8'>
                <SectionHeading
                    id='integration-title'
                    index='05'
                    label='Integration'
                    title={<>One script and <Accent>one tag.</Accent></>}
                    lead="Your game server serves the element, your backend signs a short-lived session token, and the sandbox settles bets against test balances, so you need no wallet code to start."
                />

                <div className='mt-14 grid gap-10 lg:grid-cols-12 lg:gap-12'>
                    <div className='lg:col-span-5'>
                        <ol>
                            {ONBOARDING_STEPS.map((step, index) => (
                                <li key={step.title} className='relative grid grid-cols-[44px_minmax(0,1fr)] gap-4 pb-8 last:pb-0'>
                                    {index < ONBOARDING_STEPS.length - 1 && (
                                        <span aria-hidden='true' className='absolute bottom-0 left-[21.5px] top-12 w-px bg-gradient-to-b from-white/15 to-white/5' />
                                    )}

                                    <span className='grid size-11 place-items-center rounded-full border border-white/10 bg-ink-1 font-mono text-[11px] text-bone'>
                                        {pad(index + 1)}
                                    </span>

                                    <div className='pt-2'>
                                        <h3 className='text-lg font-semibold tracking-[-0.01em] text-bone'>
                                            {step.title}
                                        </h3>
                                        <p className='mt-1 text-sm leading-relaxed text-steel'>
                                            {step.text}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        <div className='mt-10 rounded-[24px] border border-white/[0.08] bg-gradient-to-br from-white/[0.05] to-transparent p-5'>
                            <div className='flex items-center gap-3'>
                                <span className='grid size-9 place-items-center rounded-xl bg-ember/15 text-ember'>
                                    <Bot aria-hidden='true' className='size-4' />
                                </span>
                                <span className='text-base font-semibold text-bone'>Working with a coding agent?</span>
                            </div>
                            <p className='mt-3 text-sm leading-relaxed text-steel'>
                                The dashboard writes instructions your agent can follow and the docs ship an MCP server, while you keep the secrets yourself.
                            </p>
                            <div className='mt-5'>
                                <ButtonLink href={LINKS.agents} variant='ghost' size='md' external>
                                    Agent guide
                                </ButtonLink>
                            </div>
                        </div>
                    </div>

                    <div className='lg:col-span-7'>
                        <CodeWindow
                            snippets={SNIPPETS}
                            note="Example values from the docs. Your real origin, issuer and casino ID are on the dashboard's Integration page, and secrets stay in your environment."
                        />

                        <div className='mt-4 grid gap-4 sm:grid-cols-2'>
                            <a
                                href={LINKS.quickstart}
                                target='_blank'
                                rel='noreferrer'
                                className='group rounded-[22px] border border-white/[0.08] bg-ink-1 p-5 transition-colors hover:border-ember/40'
                            >
                                <span className='font-mono text-[10px] uppercase tracking-[0.16em] text-steel'>Quickstart</span>
                                <span className='mt-2 block text-base font-semibold text-bone'>From a new account to a settled sandbox bet on your own page</span>
                                <span className='sr-only'> (opens in a new tab)</span>
                            </a>

                            <a
                                href={LINKS.walletApi}
                                target='_blank'
                                rel='noreferrer'
                                className='group rounded-[22px] border border-white/[0.08] bg-ink-1 p-5 transition-colors hover:border-ember/40'
                            >
                                <span className='font-mono text-[10px] uppercase tracking-[0.16em] text-steel'>Wallet API</span>
                                <span className='mt-2 block text-base font-semibold text-bone'>Debit, credit and balance, with signatures, idempotency and retries</span>
                                <span className='sr-only'> (opens in a new tab)</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default IntegrationSection
